/**
 * CustomAutorizacaoResumo.tsx — a HISTÓRIA do caso, na lateral do pedido.
 *
 * Pedido dele, 05/09/2026:
 *
 *   "Eu continuo vendo o pedido de autorização ocupando a metade da tela. Você
 *   pode montar um painel lateral que vai mostrando o próprio acompanhamento —
 *   o acompanhamento é uma página separada, mas você pode mostrar uma timeline
 *   bem construidinha, para a pessoa entrar e já ver tudo mais simplificado."
 *
 * ─── O QUE ELE MOSTRA, E O QUE NÃO ───────────────────────────────────────────
 * Mostra o CAMINHO: quando o pedido nasceu, quando (e como) foi decidido, cada
 * etapa com o seu estado, e o encerramento. É a resposta a *"o que está
 * acontecendo com isto?"* sem trocar de aba.
 *
 * **Não é a aba Acompanhamento em miniatura**: não tem os botões, o chat, os
 * registros nem a criação de etapa. Painel que tenta fazer tudo vira uma
 * segunda tela pior que a primeira — este aponta para lá.
 *
 * ⚠️ A `.timeline` é DO TEMA (`assets/scss/style.css`, com a variante
 * `.circle` e o `.circle-dot` colorido). Estava lá, escrita e sem nenhum uso no
 * app inteiro — regra do projeto: procurar antes de inventar.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatarData } from '@/lib/data';
import type { Autorizacao } from './shared';
import { andamentoDaAutorizacao, rotuloStatus } from './shared';
import type { Etapa } from './CustomEtapas';
import { numerosDasEtapas } from './CustomEtapasLista';

/** Um ponto da linha: o que aconteceu, quando, e a cor do marcador. */
interface Passo {
  chave: string;
  titulo: string;
  detalhe?: string | null;
  quando?: string | null;
  cor: string;
  /**
   * TRÊS MOMENTOS, não dois. A primeira versão tinha só `futuro?: boolean`, e o
   * resultado foi o oposto do pretendido: numa autorização pendente, "Aguardando
   * a decisão" — a ÚNICA coisa que importa — era a linha mais fraca da lista,
   * porque ainda não tinha acontecido. Dele, 11/09: *"ele vai perdendo a cor;
   * isso para mim não faz sentido"*.
   *
   * `agora` é o que espera alguém agir: a decisão pendente, a etapa em andamento,
   * a etapa liberada. É o que tem de saltar. `passado` é sóbrio e legível;
   * `futuro` é o que ainda nem pode começar, e só esse apaga.
   */
  momento: 'passado' | 'agora' | 'futuro';
}

/* As cores dos marcadores são as MESMAS dos selos (`casaa-selo-*`): o verde da
   etapa concluída no cartão tem de ser o verde dela aqui, senão a pessoa
   aprende duas linguagens de cor para o mesmo assunto. */
const COR: Record<string, string> = {
  feita: 'casaa-selo-sucesso',
  em_andamento: 'casaa-selo-destaque',
  dispensada: 'casaa-selo-neutro',
  aberta: 'casaa-selo-neutro',
};

function passosDoCaso(aut: Autorizacao, etapas: Etapa[]): Passo[] {
  const numeros = numerosDasEtapas(etapas);
  const passos: Passo[] = [
    {
      chave: 'aberto',
      titulo: 'Pedido registrado',
      detalhe: aut.registrante_nome,
      quando: aut.criado_em,
      cor: 'casaa-selo-neutro',
      momento: 'passado',
    },
  ];

  if (aut.status === 'pendente') {
    passos.push({
      chave: 'decisao',
      titulo: 'Aguardando a decisão',
      detalhe: 'A Adriana ainda não respondeu',
      cor: 'casaa-selo-atencao',
      momento: 'agora',
    });
  } else {
    passos.push({
      chave: 'decisao',
      titulo: rotuloStatus(aut.status),
      detalhe: aut.aprovador_nome,
      quando: aut.dt_decisao,
      cor: aut.status === 'autorizada' ? 'casaa-selo-sucesso'
        : aut.status === 'negada' ? 'casaa-selo-alerta' : 'casaa-selo-neutro',
      momento: 'passado',
    });
  }

  etapas.forEach((e) => {
    const n = numeros.get(e.id);
    passos.push({
      chave: `etapa-${e.id}`,
      titulo: `${n ? `${n} ` : ''}${e.titulo}`,
      detalhe: [
        e.responsavel_nome,
        e.estado === 'feita' ? 'concluída'
          : e.estado === 'dispensada' ? 'dispensada'
          : e.estado === 'em_andamento' ? 'em andamento'
          : e.na_vez ? 'liberada' : 'esperando a anterior',
      ].filter(Boolean).join(' · '),
      quando: e.estado === 'feita' ? e.conclusao : e.prazo,
      cor: COR[e.estado] ?? 'casaa-selo-neutro',
      // Liberada é AGORA: ninguém a impede, só falta alguém pegar. Bloqueada é
      // futuro de verdade — nem pode começar.
      momento: e.estado === 'em_andamento' ? 'agora'
        : e.estado === 'aberta' ? (e.na_vez ? 'agora' : 'futuro')
        : 'passado',
    });
  });

  if (aut.encerrada) {
    passos.push({
      chave: 'fim',
      titulo: 'Encerrada',
      detalhe: aut.virou_venda ? 'virou pedido de venda' : 'não virou venda',
      quando: aut.dt_encerramento,
      cor: 'casaa-selo-neutro',
      momento: 'passado',
    });
  }
  return passos;
}

interface Props {
  aut: Autorizacao;
  etapas: Etapa[];
  /** Liga o "ver o acompanhamento" — só para quem enxerga as etapas. */
  aoVerAcompanhamento?: () => void;
}

export function CustomAutorizacaoResumo({ aut, etapas, aoVerAcompanhamento }: Props) {
  const passos = passosDoCaso(aut, etapas);
  const andamento = andamentoDaAutorizacao(aut);
  const abertas = etapas.filter((e) => e.estado === 'aberta' || e.estado === 'em_andamento');

  /* ⛔ "TEM MAIS" SE DIZ, NÃO SE INSINUA. Tentei duas formas erradas antes: a
     contagem de passos (errou na primeira tela real, porque título longo quebra
     em duas linhas) e o degradê no pé — que ele reprovou na hora, porque texto
     sumindo parece defeito, não convite. Agora a caixa mede se transbordou e o
     rodapé DIZ, com a barra de rolagem visível ao lado. */
  const corpoRef = useRef<HTMLDivElement>(null);
  const [temMais, setTemMais] = useState(false);
  useEffect(() => {
    const el = corpoRef.current;
    if (!el) return;
    const medir = () => setTemMais(el.scrollHeight > el.clientHeight + 2);
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => obs.disconnect();
  }, [passos.length]);

  return (
    <div className="card adminuiux-card mb-3">
      <div className="card-header bg-transparent d-flex align-items-center">
        <h6 className="mb-0">Como está</h6>
        {andamento && (
          <span className={`badge ms-auto ${andamento.cor}`}>{andamento.rotulo}</span>
        )}
      </div>
      <div className="card-body">
        {/* A LINHA. `circle` + `circle-dot` são do tema; a cor de cada ponto é
            a do selo do estado.

            ⛔ TETO E ROLAGEM (11/09). A lista CRESCE — uma linha por etapa — e a
            conversa logo abaixo não. Sem teto, sete etapas empurram o chat para
            fora da tela, e o chat é o que muda todo dia. O degradê no pé só
            aparece quando há o que rolar. */}
        <div className="casaa-resumo-corpo" ref={corpoRef}>
        <ul className="timeline circle mb-0">
          {passos.map((p) => (
            <li
              key={p.chave}
              /* ⛔ NADA DE ESMAECER. Dele, 11/09, depois de ver a primeira
                 tentativa: *"eu insisto que do jeito que está tá ruim porque
                 está esmaecendo; eu não gosto de esmaecendo, parece que sumiu o
                 último item"*. E é verdade: opacidade não diz "ainda não
                 aconteceu", diz "está quebrado". O que ainda não aconteceu se
                 distingue pelo MARCADOR (vazado) e por uma palavra, não por
                 ficar ilegível. */
              className={p.momento === 'futuro' ? 'casaa-resumo-futuro'
                : p.momento === 'agora' ? 'casaa-resumo-agora' : undefined}
            >
              <span className={`circle-dot ${p.cor}`}></span>
              <p className="mb-0 fw-medium">{p.titulo}</p>
              {p.detalhe && <p className="small text-secondary mb-0">{p.detalhe}</p>}
              {p.quando && (
                <p className="small text-secondary mb-0">{formatarData(p.quando.slice(0, 10))}</p>
              )}
            </li>
          ))}
        </ul>
        </div>
        {temMais && (
          <p className="small text-secondary mb-0 mt-2 casaa-resumo-role">
            <i className="bi bi-arrow-down-short"></i> Role para ver o resto
          </p>
        )}

        {etapas.length === 0 && aut.status === 'autorizada' && !aut.encerrada && (
          <p className="small text-secondary mb-0 mt-2">
            Sem etapas — este caso se resolve informando o número do pedido de venda.
          </p>
        )}
      </div>
      {aoVerAcompanhamento && etapas.length > 0 && (
        <div className="card-footer bg-transparent">
          <button type="button" className="btn btn-link btn-sm p-0" onClick={aoVerAcompanhamento}>
            Ver o acompanhamento
            {abertas.length > 0 && ` (${abertas.length} em aberto)`}
            <i className="bi bi-arrow-right ms-1"></i>
          </button>
        </div>
      )}
    </div>
  );
}

/** O rótulo da autorização na lateral — usado também pelo `Link` de volta. */
export const linkDoAcompanhamento = (id: number) => (
  <Link to={`/autorizacoes/${id}?aba=acompanhamento`}>Acompanhamento</Link>
);
